import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import logo from '../assets/logo.png';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/products' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className={`fixed w-full z-50 transition-all duration-500 border-b ${
      isScrolled
        ? 'bg-cream-50/95 backdrop-blur-md border-gold-200 shadow-md py-2'
        : 'bg-cream-50/80 backdrop-blur-sm border-transparent py-4'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="relative flex items-center bg-white rounded-lg border border-gold-200 px-2.5 py-1.5 shadow-sm transition-all duration-500 group-hover:shadow-md group-hover:border-gold-300">
              <div className="absolute inset-0 bg-gold-300/20 blur-lg rounded-lg transition-all duration-500 group-hover:bg-gold-400/30 -z-10"></div>
              <img
                src={logo}
                alt="India Glass Works And Frames logo"
                className={`relative object-contain w-auto transition-all duration-500 group-hover:scale-105 ${
                  isScrolled ? 'h-8' : 'h-10'
                }`}
              />
            </div>
            <div className="text-ink-600">
              <div className="font-display font-bold text-lg tracking-wide transition-all duration-300 group-hover:text-crimson-500">
                India Glass Works <span className="text-crimson-500">&amp; Frames</span>
              </div>
              <div className="text-[11px] text-gold-600 tracking-widest uppercase transition-all duration-300 group-hover:text-gold-700">
                The Best Framing Under One Roof
              </div>
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="relative group px-6 py-2"
              >
                <span className={`relative z-10 text-sm font-semibold tracking-wide transition-colors duration-300 ${
                  isActive(item.path) ? 'text-white' : 'text-ink-600 group-hover:text-white'
                }`}>
                  {item.name}
                </span>

                {/* Animated background */}
                <div className={`absolute inset-0 rounded-lg transition-all duration-300 ${
                  isActive(item.path)
                    ? 'bg-gradient-to-r from-crimson-500 to-crimson-600 shadow-md shadow-crimson-500/30'
                    : 'bg-transparent group-hover:bg-gradient-to-r group-hover:from-crimson-500 group-hover:to-crimson-600'
                }`}></div>
              </Link>
            ))}
            <a
              href="tel:+919359370652"
              className="ml-3 inline-flex items-center gap-2 bg-gradient-to-r from-gold-400 to-gold-500 hover:from-gold-500 hover:to-gold-600 text-ink-700 px-5 py-2.5 rounded-lg text-sm font-semibold shadow-md shadow-gold-500/30 transition-all duration-300 transform hover:scale-105"
            >
              <Phone size={16} />
              Call Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden relative group p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <div className="absolute inset-0 bg-crimson-500/0 group-hover:bg-crimson-500/10 rounded-lg transition-all duration-300"></div>
            <div className="relative text-crimson-600">
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </div>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-2 animate-fadeIn">
            {navItems.map((item, index) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                style={{ animationDelay: `${index * 100}ms` }}
                className={`block py-3 px-4 rounded-lg transition-all duration-300 ${
                  isActive(item.path)
                    ? 'bg-gradient-to-r from-crimson-500 to-crimson-600 text-white font-bold shadow-md'
                    : 'text-ink-600 hover:bg-crimson-50 hover:text-crimson-600 hover:translate-x-2'
                }`}
              >
                {item.name}
              </Link>
            ))}
            <a
              href="tel:+919359370652"
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-gold-400 to-gold-500 text-ink-700 py-3 px-4 rounded-lg font-semibold"
            >
              <Phone size={16} /> Call Now
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
